import {FC, useEffect, useMemo, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Col, Row, Spinner} from 'react-bootstrap'
import DataTable from '../../../../ui-kit/DataTable'
import {Admin} from './core/_models'
import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {ColumnDef, createColumnHelper} from '@tanstack/react-table'
import {useIntl} from 'react-intl'
import {deleteUser, getAllAdmin} from './core/_requests'
import {Link} from 'react-router-dom'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../modules/auth/core/AbilityContext'
import {showSwal} from '../../../../ui-kit'



const ManageUserRolePage: FC<{subject: string}> = ({subject}) => {
  const axiosPrivate = useAxiosPrivate()
  const [internalLoading, setInternalLoading] = useState(false)
  const ability = useAbility(AbilityContext)
  const [admins, setAdmins] = useState<Admin[]>([])
  const intl = useIntl()
  const [includeZero, setIncludeZero] = useState(false)
  const columnHelper = createColumnHelper<Admin>()

  const columns: ColumnDef<Admin, any>[] = useMemo(
    () => [
      columnHelper.accessor('username', {
        header: 'Username',
        cell: (info) => (info.getValue() ? info.getValue() : '-'),
        meta: {
          headerClass: 'text-left',
          cellClass: 'text-left',
        },
        enableSorting: true,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor('email', {
        header: 'Email',
        cell: (info) => (info.getValue() ? info.getValue() : '-'),
        meta: {
          headerClass: 'text-left',
          cellClass: 'text-left',
        },
        enableSorting: true,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor('role', {
        header: 'Role',
        cell: (info) => info.getValue() ?? '-',
        meta: {
          headerClass: 'text-left',
          cellClass: 'text-left',
        },
        enableSorting: true,
        enableGlobalFilter: true,
      }),
      columnHelper.accessor('id', {
        header: 'Actions',
        cell: (info) => (
          <div className='d-flex gap-3 justify-content-center'>
            <Link to={`/manage-user-role/${info.getValue()}`} className='link-primary'>
              <span className='fa fa-eye'></span>
            </Link>
            {ability.can('delete', subject) ? (
              <Link
                to='#'
                className='link-danger'
                onClick={() => handleUserDeletion(info.getValue())}
              >
                <span className='fa fa-trash'></span>
              </Link>
            ) : (
              ''
            )}
          </div>
        ),
        enableSorting: false,
        enableGlobalFilter: false,
      }),
    ],
    [includeZero]
  )

  const fetchAllAdmin = () => {
    setInternalLoading(true)
    getAllAdmin(axiosPrivate)
      .then((res) => {
        const resData = res.data.data
        setAdmins(resData.admins)
      })
      .catch((e) => {
        console.error(e)
      })
      .finally(() => setInternalLoading(false))
  }

  useEffect(() => {
    fetchAllAdmin()
  }, [])

  const handleUserDeletion = (id) => {
    showSwal({
      title: 'Are you sure you want to delete this user?',
      icon: 'question',
    }).then((result) => {
      if (!result.isConfirmed) return

      deleteUser(axiosPrivate, id)
        .then((res) => {
          if (res.status === 200) {
            showSwal('User deleted', undefined, 'success').then(() => {
              setAdmins((prev) => prev?.filter((item) => item.id !== id))
            })
          }
        })
        .catch((err) => {
          console.error(err)
          showSwal('Error', 'Failed to delete user.', 'error')
        })
    })
  }

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        {intl.formatMessage({id: subject})}
      </PageTitle>
      <Row className='mb-5'>
        <Col>
          <Card className='card-custom card-border'>
            <Card.Body className='d-flex align-items-center'>
              <div className='d-flex flex-column'>
                <h3 className='text-primary'>{intl.formatMessage({id: subject})}</h3>
                <div className='text-gray-700'>
                  In this module, you can <strong>assign role</strong> to a user.
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <Card.Header className='card-header-stretch'>
              <Card.Title className='w-100'>
                <div className='title-container w-100 d-flex flex-column justify-content-between'>
                  <div className='title d-flex align-items-center'>
                    <span className='fa fa-file-alt px-2'></span>
                    <h3 className='text-gray-700'>Manage User Role</h3>
                  </div>
                </div>
                {ability.can('create', subject) ? (
                  <div className='card-action'>
                    <Link
                      to={'/manage-user-role/add'}
                      className='btn btn-primary d-flex gap-4 align-items-center'
                    >
                      <span className='fa fa-plus'></span>
                      Add
                    </Link>
                  </div>
                ) : (
                  ''
                )}
              </Card.Title>
            </Card.Header>
            <Card.Body>
              {!internalLoading ? (
                <DataTable<Admin>
                  data={admins}
                  columns={columns}
                  enablePagination={true}
                  enableSorting={true}
                  enableGlobalFilter={true}
                  globalFilterPlaceholder='Search Admin Username'
                  defaultPageSize={10}
                  pageSizes={[5, 10, 20, 50, 100]}
                  className='my-custom-table-wrapper'
                />
              ) : (
                <div
                  className='d-flex align-items-center justify-content-center'
                  style={{height: '50vh'}}
                >
                  <Spinner animation='border' role='status' variant='primary' className='me-3' />
                  <div className='loading-text'>
                    Loading
                    <span className='dot'>.</span>
                    <span className='dot'>.</span>
                    <span className='dot'>.</span>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default ManageUserRolePage
